/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 80, Figma 08·11–13): the call settings switch between
 * "Аудио · Видео · Настройки" with a segmented control at the top of the
 * sheet, instead of Element Call's tab bar. Same Tab model as TabContainer.
 * On a phone the Modal around it is already a bottom sheet; its title is
 * hidden there, so the sheet shows its own (the final design has one).
 */

import { type Key, type ReactNode, useId } from "react";

import { type Tab } from "../tabs/Tabs";
import { useMediaQuery } from "../useMediaQuery";
import styles from "./IonSegmentedTabs.module.css";

interface Props<K extends Key> {
  label: string;
  /** Shown on touchscreens, where the bottom sheet hides the Modal's title. */
  title?: string;
  tab: K;
  onTabChange: (key: K) => void;
  tabs: Tab<K>[];
}

export function IonSegmentedTabs<K extends Key>({
  label,
  title,
  tab,
  onTabChange,
  tabs,
}: Props<K>): ReactNode {
  const idPrefix = useId();
  // The same test as Modal's for its bottom sheet
  const touchscreen = useMediaQuery("(hover: none) or (pointer: coarse)");
  return (
    <div className={styles.container}>
      {title && touchscreen && (
        <h2 className={styles.title} aria-hidden>
          {title}
        </h2>
      )}
      <div role="tablist" aria-label={label} className={styles.segments}>
        {tabs.map(({ key, name }) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${key}`}
            aria-controls={`${idPrefix}-panel-${key}`}
            aria-selected={key === tab}
            className={styles.segment}
            onClick={() => onTabChange(key)}
          >
            {name}
          </button>
        ))}
      </div>
      {tabs.map(({ key, content }) => (
        <div
          key={key}
          role="tabpanel"
          id={`${idPrefix}-panel-${key}`}
          aria-labelledby={`${idPrefix}-tab-${key}`}
          hidden={key !== tab}
        >
          {content}
        </div>
      ))}
    </div>
  );
}
